import { ApiError } from "@/common/lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CategorySelect } from "@/features/cost-allocations/components/CategorySelect";
import { UnusualAmountConfirmDialog } from "@/features/cost-allocations/components/UnusualAmountConfirmDialog";
import { type CostCategory, categoryLabel } from "@/features/cost-allocations/utils/categories";
import { formatMoneyDigits } from "@/features/cost-allocations/utils/formatMoney";
import { parseAmount } from "@/features/cost-allocations/utils/parseAmount";
import type { Lot } from "@/features/lots/types";
import { Plus, Trash2, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import {
  type ReceivedInvoiceAssignment,
  type ReceivedInvoiceRow,
  useCreateAssignments,
  useDeleteAssignment,
} from "../api/receivedInvoicesApi";
import { formatInvoiceAmount } from "../utils/formatInvoiceAmount";

type Props = {
  accountId: string;
  invoice: ReceivedInvoiceRow;
  assignments: ReceivedInvoiceAssignment[];
  unassignedCents: number | null;
  lots: Lot[];
  // Default de categoría para filas nuevas: la sugerencia CABYS/keywords
  // dominante de las líneas. El humano SIEMPRE decide (trampa MAXI CAT §15.6).
  suggestedCategory: CostCategory | null;
  canAssign: boolean;
};

type DraftRow = {
  key: string;
  lotId: string;
  category: CostCategory | "";
  amount: string; // canonical decimal string en la MONEDA ORIGINAL
};

// Espejo de paymentMethodFromCondicionVenta del backend: 01 contado → cash,
// 02 crédito → credit; otro valor es ambiguo y lo decide el usuario acá.
function paymentMethodFromCondicionVenta(condicionVenta: string | null): "cash" | "credit" | null {
  if (condicionVenta === "01") return "cash";
  if (condicionVenta === "02") return "credit";
  return null;
}

function centsFromCanonical(amount: string): number {
  if (amount === "") return 0;
  return Math.round(Number(amount) * 100);
}

export function SplitBuilder({
  accountId,
  invoice,
  assignments,
  unassignedCents,
  lots,
  suggestedCategory,
  canAssign,
}: Props) {
  const createMutation = useCreateAssignments(accountId, invoice.id);
  const deleteMutation = useDeleteAssignment(accountId, invoice.id);

  const [rows, setRows] = useState<DraftRow[]>([]);
  const [paymentMethod, setPaymentMethod] = useState<"cash" | "credit" | "">("");
  const [error, setError] = useState<string | null>(null);
  const [unusualMessage, setUnusualMessage] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const totalCents = invoice.totalCents;
  if (totalCents === null) return null;

  const inferredPaymentMethod = paymentMethodFromCondicionVenta(invoice.condicionVenta);
  const needsPaymentMethod = inferredPaymentMethod === null;

  const assignedCents = assignments.reduce((sum, a) => sum + a.amountCents, 0);
  const baseRemaining = unassignedCents ?? totalCents - assignedCents;
  const draftCents = rows.reduce((sum, r) => sum + centsFromCanonical(r.amount), 0);
  const remaining = baseRemaining - draftCents;
  const exceedsTotal = remaining < 0;

  const isUsd = invoice.currency !== null && invoice.currency !== "CRC";
  const exchangeRate = isUsd ? Number(invoice.exchangeRate) : 1;

  const fullyAssigned = baseRemaining <= 0;
  const activeLots = lots.filter((l) => l.isActive);

  function addRow() {
    setRows((prev) => [
      ...prev,
      {
        key: crypto.randomUUID(),
        lotId: "",
        category: suggestedCategory ?? "",
        amount: "",
      },
    ]);
  }

  function updateRow(key: string, patch: Partial<DraftRow>) {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }

  function removeRow(key: string) {
    setRows((prev) => prev.filter((r) => r.key !== key));
  }

  const rowsIncomplete = rows.some(
    (r) => r.lotId === "" || r.category === "" || centsFromCanonical(r.amount) <= 0,
  );
  const submitDisabled =
    createMutation.isPending ||
    rows.length === 0 ||
    rowsIncomplete ||
    exceedsTotal ||
    (needsPaymentMethod && paymentMethod === "");

  async function submitWith(confirmUnusual: boolean) {
    if (createMutation.isPending) return;
    setError(null);

    try {
      await createMutation.mutateAsync({
        assignments: rows.map((r) => ({
          lotId: r.lotId,
          category: r.category as CostCategory,
          amountCents: centsFromCanonical(r.amount),
        })),
        ...(needsPaymentMethod && paymentMethod !== "" && { paymentMethod }),
        confirmUnusual,
      });
      toast.success("Asignación registrada — el costo quedó prorrateado.");
      setUnusualMessage(null);
      setRows([]);
    } catch (e) {
      if (e instanceof ApiError && e.code === "UNUSUAL_AMOUNT") {
        setUnusualMessage(e.message);
        return;
      }
      if (confirmUnusual) setUnusualMessage(null);
      setError(e instanceof ApiError ? e.message : "Ocurrió un error inesperado.");
    }
  }

  async function handleDeleteConfirmed() {
    if (confirmDeleteId === null || deleteMutation.isPending) return;
    setDeleteError(null);
    try {
      await deleteMutation.mutateAsync(confirmDeleteId);
      toast.success("Asignación eliminada.");
      setConfirmDeleteId(null);
    } catch (e) {
      setConfirmDeleteId(null);
      // 409 PERIOD_SEALED / EVENT_CORRECTED llegan con mensaje claro del backend.
      setDeleteError(e instanceof ApiError ? e.message : "No se pudo eliminar la asignación.");
    }
  }

  return (
    <section
      aria-label="Asignación a lotes"
      className="rounded-2xl border border-app-border bg-app-surface p-4"
    >
      <h2 className="font-medium text-[11px] text-app-ink-3 uppercase tracking-[0.06em]">
        Asignación a lotes
      </h2>

      {assignments.length > 0 && (
        <ul className="mt-3 flex flex-col gap-2">
          {assignments.map((a) => (
            <li
              key={a.id}
              className="flex items-center justify-between gap-3 rounded-xl border border-app-border bg-app-surface-2 px-3 py-2"
            >
              <div className="min-w-0">
                <div className="truncate font-medium text-[13px] text-app-ink-1">
                  {lotName(lots, a.lotId)}
                </div>
                <div className="text-[12px] text-app-ink-3">
                  {a.category ? categoryLabel(a.category) : "—"}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <span className="font-medium text-[13px] text-app-ink-1 tabular-nums">
                  {formatInvoiceAmount(a.amountCents, invoice.currency)}
                </span>
                {canAssign && (
                  <button
                    type="button"
                    aria-label={`Eliminar asignación a ${lotName(lots, a.lotId)}`}
                    onClick={() => {
                      setDeleteError(null);
                      setConfirmDeleteId(a.id);
                    }}
                    className="grid h-8 w-8 place-items-center rounded-lg border border-app-border bg-app-surface text-app-ink-3 transition-colors hover:bg-rose-50 hover:text-rose-600"
                  >
                    <Trash2 size={14} aria-hidden="true" />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {deleteError && (
        <p
          role="alert"
          className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-700"
        >
          {deleteError}
        </p>
      )}

      {canAssign && !fullyAssigned && (
        <div className="mt-3 flex flex-col gap-3">
          {rows.map((row) => (
            <div
              key={row.key}
              className="flex flex-col gap-2 rounded-xl border border-app-border p-3"
            >
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor={`split-lot-${row.key}`}
                    className="font-medium text-[12px] text-app-ink-3"
                  >
                    Lote
                  </label>
                  <Select value={row.lotId} onValueChange={(v) => updateRow(row.key, { lotId: v })}>
                    <SelectTrigger
                      id={`split-lot-${row.key}`}
                      className="h-10 w-full text-app-ink-1"
                      aria-label="Lote"
                    >
                      <SelectValue placeholder="Selecciona un lote" />
                    </SelectTrigger>
                    <SelectContent>
                      {activeLots.length === 0 ? (
                        <div className="px-3 py-2 text-[13px] text-app-ink-3">
                          No hay lotes activos.
                        </div>
                      ) : (
                        activeLots.map((lot) => (
                          <SelectItem key={lot.id} value={lot.id}>
                            {lot.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex flex-col gap-1">
                  <label
                    htmlFor={`split-category-${row.key}`}
                    className="font-medium text-[12px] text-app-ink-3"
                  >
                    Categoría
                  </label>
                  <CategorySelect
                    id={`split-category-${row.key}`}
                    value={row.category}
                    onChange={(c) => updateRow(row.key, { category: c })}
                    aria-label="Categoría"
                    placeholder="Selecciona una categoría"
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label
                    htmlFor={`split-amount-${row.key}`}
                    className="font-medium text-[12px] text-app-ink-3"
                  >
                    Monto
                  </label>
                  <SplitAmountInput
                    id={`split-amount-${row.key}`}
                    symbol={isUsd ? "$" : "₡"}
                    value={row.amount}
                    onChange={(v) => updateRow(row.key, { amount: v })}
                  />
                  {isUsd && row.amount !== "" && Number.isFinite(exchangeRate) && (
                    <span className="text-[11px] text-app-ink-3">
                      ≈{" "}
                      {formatInvoiceAmount(
                        Math.round(centsFromCanonical(row.amount) * exchangeRate),
                        "CRC",
                      )}{" "}
                      al tipo de cambio del XML
                    </span>
                  )}
                </div>
              </div>

              <button
                type="button"
                onClick={() => removeRow(row.key)}
                className="inline-flex items-center gap-1 self-end text-[12px] text-app-ink-3 hover:text-app-ink-1"
              >
                <X size={12} aria-hidden="true" />
                Quitar fila
              </button>
            </div>
          ))}

          <button
            type="button"
            onClick={addRow}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-app-border border-dashed bg-app-surface px-4 font-medium text-[13px] text-app-ink-2 transition-colors hover:bg-app-surface-2 hover:text-app-ink-1"
          >
            <Plus size={14} aria-hidden="true" />
            Agregar asignación
          </button>

          {needsPaymentMethod && rows.length > 0 && (
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="split-payment-method"
                className="font-medium text-[13px] text-app-ink-2"
              >
                Forma de pago
              </label>
              <Select
                value={paymentMethod}
                onValueChange={(v) => setPaymentMethod(v as "cash" | "credit")}
              >
                <SelectTrigger
                  id="split-payment-method"
                  className="h-10 w-full text-app-ink-1"
                  aria-label="Forma de pago"
                >
                  <SelectValue placeholder="Elegí contado o crédito" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="credit">Crédito</SelectItem>
                  <SelectItem value="cash">Contado</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-[12px] text-app-ink-3">
                La factura no trae condición de venta clara — indicá cómo se pagó.
              </span>
            </div>
          )}
        </div>
      )}

      {/* SIN ASIGNAR: siempre visible — lo no ganadero (ej. alimento de gato
          del agroservicio) se queda acá y NO genera asiento. */}
      <output
        aria-label="Monto sin asignar"
        className={`mt-3 flex items-center justify-between rounded-xl px-3 py-2 text-[13px] ${
          exceedsTotal ? "bg-rose-50 font-medium text-rose-700" : "bg-app-surface-2 text-app-ink-2"
        }`}
      >
        <span>Sin asignar</span>
        <span className="font-medium tabular-nums">
          {formatInvoiceAmount(Math.max(remaining, 0), invoice.currency)}
        </span>
      </output>

      {exceedsTotal && (
        <p role="alert" className="mt-2 text-[13px] text-rose-700">
          La suma de asignaciones supera el total de la factura por{" "}
          {formatInvoiceAmount(Math.abs(remaining), invoice.currency)}.
        </p>
      )}

      {error && (
        <p
          role="alert"
          className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-700"
        >
          {error}
        </p>
      )}

      {canAssign && !fullyAssigned && rows.length > 0 && (
        <button
          type="button"
          disabled={submitDisabled}
          onClick={() => void submitWith(false)}
          className="mt-4 inline-flex h-10 w-full items-center justify-center rounded-xl bg-app-ink-1 px-4 font-medium text-[14px] text-white transition-colors hover:bg-[#1e211d] disabled:opacity-50 sm:w-auto"
        >
          {createMutation.isPending ? "Asignando…" : "Asignar a lotes"}
        </button>
      )}

      {fullyAssigned && (
        <p className="mt-3 text-[13px] text-app-ink-3">
          La factura ya está completamente asignada.
        </p>
      )}

      <Dialog
        open={confirmDeleteId !== null}
        onOpenChange={(o) => {
          if (!o) setConfirmDeleteId(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Eliminar asignación</DialogTitle>
            <DialogDescription>
              Se elimina también el costo prorrateado que generó. Si el período ya fue sellado, no
              se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button
              type="button"
              disabled={deleteMutation.isPending}
              onClick={() => setConfirmDeleteId(null)}
              className="inline-flex h-10 items-center justify-center rounded-xl border border-app-border bg-app-surface px-4 font-medium text-[14px] text-app-ink-1 transition-colors hover:bg-app-surface-2 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={deleteMutation.isPending}
              onClick={() => void handleDeleteConfirmed()}
              className="inline-flex h-10 items-center justify-center rounded-xl bg-rose-600 px-4 font-medium text-[14px] text-white transition-colors hover:bg-rose-700 disabled:opacity-50"
            >
              {deleteMutation.isPending ? "Eliminando…" : "Sí, eliminar"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UnusualAmountConfirmDialog
        open={unusualMessage !== null}
        onOpenChange={(o) => {
          if (!o) setUnusualMessage(null);
        }}
        onConfirm={() => void submitWith(true)}
        message={unusualMessage ?? ""}
        pending={createMutation.isPending}
      />
    </section>
  );
}

function lotName(lots: Lot[], lotId: string | null): string {
  if (!lotId) return "—";
  return lots.find((l) => l.id === lotId)?.name ?? "Lote desconocido";
}

// Input de monto en la moneda ORIGINAL de la factura. No reusa AmountInput de
// costos porque aquel tiene el ₡ fijo — acá el símbolo depende de la moneda.
function SplitAmountInput({
  id,
  symbol,
  value,
  onChange,
}: {
  id: string;
  symbol: string;
  value: string;
  onChange: (canonical: string) => void;
}) {
  const [display, setDisplay] = useState(() => (value ? formatMoneyDigits(value) : ""));

  return (
    <div className="relative">
      <span
        className="pointer-events-none absolute inset-y-0 left-3 flex items-center font-medium text-[15px] text-app-ink-2"
        aria-hidden="true"
      >
        {symbol}
      </span>
      <input
        id={id}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        value={display}
        aria-label="Monto"
        onChange={(e) => setDisplay(e.target.value)}
        onBlur={() => {
          const canonical = parseAmount(display);
          if (canonical === null) {
            setDisplay("");
            onChange("");
          } else {
            onChange(canonical);
            setDisplay(formatMoneyDigits(canonical));
          }
        }}
        className="h-10 w-full rounded-lg border border-app-border bg-app-surface pr-3 pl-7 text-app-ink-1 text-base outline-none focus-visible:border-app-emerald focus-visible:ring-3 focus-visible:ring-app-emerald/20"
      />
    </div>
  );
}
